import { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { useT } from '../../i18n';
import { SECTION_TONE } from './nav-config';
import type { NavItem } from './nav-config';

interface SidebarFlyoutProps {
  label: string;
  icon: LucideIcon;
  /** Các mục con. Tên là `muc` chứ không `children`: đây là dữ liệu, không
   *  phải nội dung JSX, mà `children` truyền như prop thường thì React cảnh báo. */
  muc: readonly NavItem[];
  hasActiveChild: boolean;
}

/**
 * Cụm con khi thanh **đang gập**: mở ra bên cạnh thay vì mở xuống dưới.
 *
 * Trước đây thanh gập thì mọi cụm con bị đóng cứng và **không có cách nào mở** —
 * muốn vào một màn trong cụm thì phải mở lại cả thanh. Ghi chú cũ trong
 * `Sidebar.tsx` giải thích đúng lý do không mở xuống dưới (59 mục xếp dọc thì
 * tràn khỏi màn hình, và biểu tượng con không nhãn trông y hệt biểu tượng cha),
 * nhưng kết luận "luôn đóng" làm rail 76px thành đường cùng. Mở ngang thì
 * không tốn chiều cao nào và mỗi mục con vẫn có nhãn chữ.
 *
 * Mở bằng cả **trỏ chuột và tiêu điểm bàn phím** (`focus-within`), nên người
 * dùng Tab tới cũng thấy đúng cụm đó — mở chỉ bằng `:hover` là bỏ rơi bàn phím.
 */
export function SidebarFlyout({
  label,
  icon: Icon,
  muc,
  hasActiveChild,
}: SidebarFlyoutProps) {
  const { t } = useT();
  const [mo, setMo] = useState(false);
  const nut = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();

  /*
   * Đổi route thì đóng tấm.
   *
   * Bấm một mục con bằng **bàn phím** thì tiêu điểm nằm lại trên liên kết vừa
   * mở, trong tấm vẫn đang mở — nên tấm 208px che nội dung suốt lúc chuyển
   * trang. Chuột thì `mouseleave` lo, nhưng bàn phím không có sự kiện tương
   * đương.
   */
  useEffect(() => {
    setMo(false);
  }, [pathname]);

  return (
    <div
      className="relative px-1.5"
      onMouseEnter={() => setMo(true)}
      onMouseLeave={() => setMo(false)}
      onFocus={() => setMo(true)}
      onBlur={(e) => {
        // Chỉ đóng khi tiêu điểm rời khỏi **cả** cụm, không phải khi nhảy giữa
        // hai mục con bên trong.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMo(false);
      }}
      onKeyDown={(e) => {
        // Esc đóng tấm và trả tiêu điểm về nút: không có đường này thì người
        // dùng bàn phím mở ra rồi phải Tab hết các mục con mới ra được.
        if (e.key !== 'Escape' || !mo) return;
        e.preventDefault();
        e.stopPropagation();
        setMo(false);
        nut.current?.focus();
      }}
    >
      <button
        ref={nut}
        type="button"
        aria-expanded={mo}
        aria-label={label}
        title={label}
        className={cn(
          'group relative flex w-full items-center justify-center rounded-lg py-2 transition-colors',
          hasActiveChild
            ? 'bg-subtle text-content-primary'
            : 'text-content-secondary hover:bg-subtle hover:text-content-primary',
        )}
      >
        <Icon size={18} className={cn('relative z-10 shrink-0', SECTION_TONE)} />
        {/*
          Chấm nhỏ ở góc: dấu hiệu "còn thứ bên trong". Biểu tượng cha và biểu
          tượng lá giống nhau hệt khi không có nhãn, nên nếu thiếu dấu này thì
          không có gì nói cho người dùng biết chỗ nào bấm được ra thêm.
        */}
        <span
          aria-hidden
          className={cn(
            'absolute bottom-1 right-1.5 h-1 w-1 rounded-full',
            hasActiveChild ? 'bg-primary-600' : 'bg-content-muted opacity-60',
          )}
        />
      </button>

      {mo && (
        <div
          // `left-full` + `ml-1` đặt tấm ngay cạnh rail. `z-30` để nằm trên nút
          // gập (`z-20`) ở mép phải.
          className="absolute left-full top-0 z-30 ml-1 min-w-[208px] rounded-lg border border-default bg-surface py-1.5 shadow-lg"
        >
          <p className="px-3 pb-1 pt-0.5 text-2xs font-bold uppercase tracking-[0.08em] text-content-muted">
            {label}
          </p>
          {muc.map((con) => (
            <NavLink
              key={con.href ?? con.khoa}
              to={con.href!}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-3 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'font-semibold text-primary-700'
                    : 'text-content-secondary hover:bg-subtle hover:text-content-primary',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <con.icon
                    size={15}
                    className={cn('shrink-0', isActive ? 'text-primary-600' : SECTION_TONE)}
                  />
                  <span className="truncate">{con.nhanThang ?? t(con.khoa)}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
